import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const businessAreas = [
  {
    number: "01",
    title: "Software Engineering",
    description: "Digital platforms, enterprise applications and custom software engineered for performance and scale.",
  },
  {
    number: "02",
    title: "Artificial Intelligence",
    description: "Applied AI, intelligent automation, AI integrations and intelligent business systems.",
  },
  {
    number: "03",
    title: "Digital Products",
    description: "Web, mobile and scalable digital experiences built around real user needs.",
  },
  {
    number: "04",
    title: "Technology Operations",
    description: "Long-term engineering support, maintenance, modernization and technology operations.",
  },
];

const projects = [
  {
    name: "Sipena Logistics",
    category: "Logistics operations · Web + Mobile",
    description: "A unified operations platform for daily worksheets, task assignment, job tracking, deliveries and digital invoicing.",
    tags: ["Operations platform", "Mobile application", "AI-enabled workflows"],
    visual: "route",
  },
  {
    name: "SchoolseLibrary",
    category: "Education technology · AI",
    description: "An AI-enabled digital library designed to improve content discovery through smart search and relevant learning recommendations.",
    tags: ["Education platform", "Intelligent search", "Content systems"],
    visual: "library",
  },
  {
    name: "iEducate Academy",
    category: "School management · Web platform",
    description: "A central system for student records, attendance, fees, examinations and internal school communication.",
    tags: ["Management system", "Web application", "Education operations"],
    visual: "education",
  },
];

const capabilities = [
  ["Frontend", "React", "Next.js", "Vue", "TypeScript"],
  ["Backend", "Node.js", "Laravel", "APIs", "Data systems"],
  ["Mobile", "React Native", "Flutter", "Cross-platform systems"],
  ["AI", "LLM integration", "AI automation", "RAG", "Intelligent assistants"],
  ["Cloud & Infrastructure", "AWS", "Firebase", "Vercel", "Modern deployment infrastructure"],
];

function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add("is-visible");
          observer.unobserve(node);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function Reveal({ children, className = "" }) {
  const ref = useReveal();
  return <div ref={ref} className={`reveal ${className}`}>{children}</div>;
}

function SectionIntro({ eyebrow, title, copy, light = false }) {
  return (
    <Reveal className={`section-intro${light ? " section-intro--light" : ""}`}>
      <p className="eyebrow">{eyebrow}</p>
      <div className="section-intro__content">
        <h2>{title}</h2>
        {copy && <p>{copy}</p>}
      </div>
    </Reveal>
  );
}

function HeroVisual() {
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hero-visual__grid" />
      <svg viewBox="0 0 620 620" role="presentation">
        <circle className="orbit orbit--outer" cx="310" cy="310" r="238" />
        <circle className="orbit orbit--inner" cx="310" cy="310" r="152" />
        <path className="hero-c" d="M430 186A170 170 0 1 0 430 434" />
        <path className="hero-axis" d="M78 310H542M310 78V542" />
        <rect className="hero-node hero-node--one" x="446" y="246" width="28" height="28" />
        <rect className="hero-node hero-node--two" x="489" y="294" width="16" height="16" />
        <rect className="hero-node hero-node--three" x="443" y="332" width="20" height="20" />
      </svg>
      <p>Engineering systems<br />for real-world scale</p>
    </div>
  );
}

function ProjectVisual({ type, index }) {
  return (
    <div className={`project-visual project-visual--${type}`} aria-hidden="true">
      <span className="project-index">0{index + 1}</span>
      {type === "route" && (
        <>
          <div className="route-map" />
          <span className="route-point route-point--a" />
          <span className="route-point route-point--b" />
          <span className="route-point route-point--c" />
          <div className="route-panel"><span /> <span /> <span /></div>
        </>
      )}
      {type === "library" && (
        <>
          <div className="library-spine library-spine--one" />
          <div className="library-spine library-spine--two" />
          <div className="library-spine library-spine--three" />
          <div className="library-search">AI / SEARCH</div>
        </>
      )}
      {type === "education" && (
        <>
          <div className="edu-chart"><i /><i /><i /><i /><i /></div>
          <div className="edu-list"><span /><span /><span /></div>
        </>
      )}
    </div>
  );
}

export default function CrovianHome() {
  return (
    <main id="top">
      <Helmet>
        <title>Crovian Technologies — Software, AI & Digital Systems</title>
        <meta name="description" content="Crovian designs and engineers software, intelligent systems and digital products built for real-world scale." />
        <link rel="canonical" href="https://croviantech.com/" />
      </Helmet>

      <section className="hero section-pad">
        <div className="hero__content">
          <p className="hero__kicker">Software · AI · Digital Systems</p>
          <h1>WE BUILD<br />TECHNOLOGY<br />FOR <span>WHAT COMES NEXT.</span></h1>
          <div className="hero__bottom">
            <p>Crovian designs and engineers technology that helps organizations build, operate and evolve.</p>
            <a className="text-link" href="#company">Explore Crovian <span aria-hidden="true">↘</span></a>
          </div>
        </div>
        <HeroVisual />
        <div className="hero__rail" aria-hidden="true"><span>01</span><i /><span>07</span></div>
      </section>

      <section className="company-statement section-pad" id="company">
        <Reveal>
          <p className="eyebrow">Crovian / Company</p>
          <h2>Technology should<br />move businesses <em>forward.</em></h2>
          <div className="company-statement__copy">
            <p>We build software, intelligent systems and digital products designed for real-world scale.</p>
            <p>Our work joins precise engineering with clear product thinking—creating technology that remains useful as organizations evolve.</p>
          </div>
        </Reveal>
      </section>

      <section className="business section-pad" id="business">
        <SectionIntro eyebrow="Our Business" title="Four disciplines. One technology company." copy="From core systems to applied intelligence, our businesses work together around the problem—not the template." />
        <div className="business-list">
          {businessAreas.map((area) => (
            <Reveal className="business-row" key={area.number}>
              <span className="business-row__number">{area.number}</span>
              <h3>{area.title}</h3>
              <p>{area.description}</p>
              <span className="business-row__arrow" aria-hidden="true">↗</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ai-principle" id="ai">
        <div className="ai-principle__visual" aria-hidden="true">
          <div className="ai-field">
            {Array.from({ length: 28 }).map((_, index) => <i key={index} />)}
          </div>
          <span>INTELLIGENCE / APPLIED</span>
        </div>
        <Reveal className="ai-principle__copy">
          <p className="eyebrow">Applied Intelligence</p>
          <h2>AI belongs inside the work.</h2>
          <p>We integrate intelligence into products and operations where it can reduce friction, support decisions and create measurable utility.</p>
          <div className="ai-principle__topics">
            <span>Automation</span><span>Knowledge systems</span><span>Intelligent assistants</span><span>AI integration</span>
          </div>
        </Reveal>
      </section>

      <section className="work section-pad" id="work">
        <SectionIntro eyebrow="Selected Work" title="Systems built around real operations." copy="A selection of digital products and platforms from the existing Crovian portfolio." />
        <div className="project-list">
          {projects.map((project, index) => (
            <Reveal className={`project${index % 2 ? " project--reverse" : ""}`} key={project.name}>
              <ProjectVisual type={project.visual} index={index} />
              <div className="project__copy">
                <p className="project__category">{project.category}</p>
                <h3>{project.name}</h3>
                <p>{project.description}</p>
                <ul>{project.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
                <Link className="text-link" to="/contact">Discuss a similar system <span aria-hidden="true">→</span></Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="kai-lab" id="kai-lab">
        <div className="kai-lab__header">
          <p>CROVIAN / <strong>KAI LAB</strong></p>
          <span>Education AI initiative</span>
        </div>
        <div className="kai-lab__body">
          <Reveal className="kai-lab__title">
            <h2>BUILDING THE FUTURE<br />OF AI IN EDUCATION.</h2>
            <p>KAI LAB develops AI-powered systems designed for schools, teachers and modern learning environments.</p>
            <a className="text-link text-link--light" href="https://kailab.in/" target="_blank" rel="noopener noreferrer">Discover KAI LAB <span aria-hidden="true">→</span></a>
          </Reveal>
          <div className="kai-products">
            {[
              ["01", "ClassOS", "School intelligence infrastructure"],
              ["02", "Navi1", "AI-guided learning and career pathways"],
              ["03", "LanguageOS", "Adaptive language learning systems"],
            ].map(([number, name, description]) => (
              <div className="kai-product" key={name}>
                <span>{number}</span><h3>{name}</h3><p>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="capabilities section-pad" id="capabilities">
        <SectionIntro eyebrow="Engineering Capabilities" title="A focused technology stack, chosen for the system." copy="We use modern, dependable tools without turning technology selection into a logo collection." />
        <div className="capability-grid">
          {capabilities.map(([category, ...items]) => (
            <Reveal className="capability-group" key={category}>
              <h3>{category}</h3>
              <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="experience section-pad" id="company-experience">
        <div className="experience__grid" aria-hidden="true">
          <i /><i /><i /><i /><i /><i />
        </div>

        <div className="experience__top">
          <p className="eyebrow">Company Experience</p>
          <p>From first release to long-term evolution</p>
        </div>

        <div className="experience__main">
          <Reveal className="experience__number">
            <span className="experience__zero">0</span>
            <strong>8</strong>
            <sup>+</sup>
          </Reveal>
          <Reveal className="experience__copy">
            <p className="experience__label">Years building software</p>
            <h2>ENGINEERING THROUGH<br />EVERY STAGE OF CHANGE.</h2>
            <p>Experience across product design, software delivery, intelligent systems and the operational work that keeps technology moving.</p>
          </Reveal>
        </div>

        <div className="experience__disciplines" aria-label="Company disciplines">
          {[
            ["01", "Web", "Platforms & applications"],
            ["02", "Mobile", "Connected experiences"],
            ["03", "AI", "Applied intelligence"],
            ["04", "Enterprise", "Systems & operations"],
          ].map(([number, title, detail]) => (
            <div className="experience-discipline" key={title}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{detail}</p>
              <i aria-hidden="true">↗</i>
            </div>
          ))}
        </div>
      </section>

      <section className="insights section-pad" id="insights">
        <SectionIntro eyebrow="Insights" title="Thinking around technology in practice." copy="A future editorial space for field notes from our work across engineering, products and education AI." />
        <div className="insight-grid">
          {["Artificial Intelligence", "Software Engineering", "Education AI"].map((topic, index) => (
            <div className="insight-item" key={topic}>
              <span>0{index + 1}</span>
              <p>Field notes / Coming soon</p>
              <h3>{topic}</h3>
              <i aria-hidden="true">↗</i>
            </div>
          ))}
        </div>
      </section>

      <section className="closing-cta">
        <p className="eyebrow">Start a conversation</p>
        <div>
          <h2>LET'S BUILD<br />WHAT COMES NEXT.</h2>
          <Link to="/contact" className="circle-link" aria-label="Start a conversation"><span>→</span></Link>
        </div>
      </section>
    </main>
  );
}
